import { Button } from '@/components/ui/button';
import { PAGE_SIZES, pageCount, pageWindow } from '@/lib/listQuery';

export interface ListPaginationProps {
  page: number;
  limit: number;
  total: number;
  onPage: (page: number) => void;
  onLimit: (limit: number) => void;
}

/** "Showing A–B of N", page-size select and numbered pages. Renders nothing when there are no rows. */
export function ListPagination({ page, limit, total, onPage, onLimit }: ListPaginationProps) {
  if (total === 0) return null;
  const pages = pageCount(total, limit);
  const current = Math.min(page, pages);
  const from = Math.min((current - 1) * limit + 1, total);
  const to = Math.min(current * limit, total);
  const atStart = current <= 1;
  const atEnd = current >= pages;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
      <span>Showing {from}–{to} of {total}</span>
      <div className="flex flex-wrap items-center gap-3">
        <select
          aria-label="Rows per page"
          value={limit}
          onChange={(e) => onLimit(Number(e.target.value))}
          className="h-8 rounded-lg border border-input bg-background px-2 text-sm text-foreground outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
        <nav aria-label="Pagination" className="flex flex-wrap items-center gap-1">
          <Button
            type="button" variant="outline" size="sm" aria-label="Previous page" aria-disabled={atStart || undefined}
            onClick={() => { if (!atStart) onPage(current - 1); }}
          >
            Prev
          </Button>
          {pageWindow(current, pages).map((p, i) =>
            p === 'gap' ? (
              <span key={`gap-${i}`} aria-hidden="true" className="px-1">…</span>
            ) : (
              <Button
                key={p} type="button" size="sm" variant={p === current ? 'default' : 'ghost'}
                aria-label={`Page ${p}`} aria-current={p === current ? 'page' : undefined}
                className="min-w-7 tabular-nums"
                onClick={() => { if (p !== current) onPage(p); }}
              >
                {p}
              </Button>
            ),
          )}
          <Button
            type="button" variant="outline" size="sm" aria-label="Next page" aria-disabled={atEnd || undefined}
            onClick={() => { if (!atEnd) onPage(current + 1); }}
          >
            Next
          </Button>
        </nav>
      </div>
    </div>
  );
}
